import {
    analyzeTable,
    buildMultiDatapoints,
    getColumnLabelById as coreGetColumnLabelById,
    getSafeSelection,
    normalizeTableView,
    type NormalizedTableView,
    type TableAnalysis,
} from '@/lib/chart-config';
import { normalizeTableBlock } from '@/lib/table-utils';
import type { ChartDatapoints, ChartSource, TableBlock } from '@/types/report-payload';

/**
 * Frontend adapter over the shared chart core (`chart-config.ts`).
 *
 * The chart extraction "brain" (column/metric detection, number parsing, axis
 * defaults, datapoint building) lives in the mirrored core so the preview and
 * the exported .docx resolve identical charts. This module only turns a report
 * `TableBlock` into the plain `NormalizedTableView` the core consumes, and
 * re-exposes the core helpers under the names the wizard/preview already use.
 */
export const toNormalizedTableView = (block?: TableBlock | null): NormalizedTableView =>
    // Upgrade any legacy shape (front-only, impure id generation) then delegate
    // the merged-cell view building to the mirrored core so the .docx matches.
    normalizeTableView(block ? normalizeTableBlock(block) : null);

export const extractTableChartData = (block?: TableBlock | null): TableAnalysis =>
    analyzeTable(toNormalizedTableView(block));

export const getSafeTableChartSelection = (
    chartData: TableAnalysis,
    xAxisColumnId?: string,
    yAxisColumnId?: string,
    preferredMeasurementType?: string,
) => getSafeSelection(chartData, xAxisColumnId, yAxisColumnId, preferredMeasurementType);

export const getColumnLabelById = (chartData: TableAnalysis, columnId?: string | null) =>
    coreGetColumnLabelById(chartData, columnId);

/**
 * Multi-series variant: builds the stored `ChartDatapoints` for N y columns —
 * a shared `x_axis`, `y_axis` = the first series (legacy readers), and a
 * `series[]` with one entry per column (labelled from its header).
 */
export const buildTableChartMultiDatapoints = (
    block: TableBlock,
    source: Pick<ChartSource, 'x_axis_column_id' | 'y_axis_column_ids' | 'skip_empty'>,
): ChartDatapoints => {
    const view = toNormalizedTableView(block);
    const analysis = analyzeTable(view);
    const { labels, series } = buildMultiDatapoints(
        view,
        analysis,
        source.x_axis_column_id,
        source.y_axis_column_ids ?? [],
        source.skip_empty,
    );
    return {
        x_axis: labels,
        y_axis: series[0]?.values ?? [],
        series: series.map((s) => ({
            label: coreGetColumnLabelById(analysis, s.columnId) || 'Valor',
            values: s.values,
        })),
    };
};

/**
 * Why did a configured chart plot nothing? Returns a user-facing (pt-BR)
 * reason for an EMPTY chart, or `null` when the chart simply isn't configured
 * yet (no table / no columns picked) — callers keep their generic
 * "configure me" copy for that case.
 */
export const explainEmptyChart = (
    block: TableBlock | null | undefined,
    source: Partial<
        Pick<ChartSource, 'x_axis_column_id' | 'y_axis_column_id' | 'y_axis_column_ids'>
    >,
): string | null => {
    if (!block) {
        return 'A tabela vinculada ao gráfico não foi encontrada. Selecione outra tabela de origem.';
    }

    const view = toNormalizedTableView(block);
    if (view.rows.length === 0) {
        return 'A tabela de origem ainda não tem linhas de dados para plotar.';
    }

    const yIds = source.y_axis_column_ids?.length
        ? source.y_axis_column_ids
        : source.y_axis_column_id
          ? [source.y_axis_column_id]
          : [];
    if (!source.x_axis_column_id || yIds.length === 0) return null;

    const analysis = analyzeTable(view);
    const xColumn = analysis.columns.find((c) => c.id === source.x_axis_column_id);
    const yColumns = analysis.columns.filter((c) => yIds.includes(c.id));
    if (!xColumn || yColumns.length === 0) {
        return 'As colunas selecionadas não existem mais na tabela. Reconfigure o gráfico.';
    }

    if (yColumns.every((c) => c.numericValueCount === 0)) {
        const labels = yColumns.map((c) => `“${c.label}”`).join(', ');
        return yColumns.length === 1
            ? `A coluna ${labels} não tem valores numéricos — o gráfico precisa de números para plotar.`
            : `As colunas ${labels} não têm valores numéricos — o gráfico precisa de números para plotar.`;
    }

    // Rows and numbers both exist, so the pairing failed: every numeric value
    // sits on a row whose X label is empty (those rows are skipped).
    return 'Nenhuma linha combina um rótulo no eixo X com um valor numérico no eixo Y — confira as colunas selecionadas.';
};
